import { isPlatformBrowser } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { Inject, Injectable, PLATFORM_ID } from '@angular/core';
import { BehaviorSubject, Observable, firstValueFrom } from 'rxjs';
import { DropshipCart, DropshipCartAttribute, DropshipCartCreateRequest } from 'shared';
import { environment } from '../../environments/environment';
import { UnifiedCartUIService } from '../shared/unified-cart/unified-cart-ui.service';

const STORAGE_KEY = 'exclusivesCartId';
const API_BASE = environment.dropshipApiBase;
const CART_ATTRIBUTES: DropshipCartAttribute[] = [
  { key: 'source', value: 'russellsny.com' }
];

export class CartResetError extends Error {
  constructor() {
    super('Exclusives cart was reset by the upstream Shopify backend.');
    this.name = 'CartResetError';
  }
}

@Injectable({ providedIn: 'root' })
export class ExclusivesCartService {
  private readonly cart$ = new BehaviorSubject<DropshipCart | null>(null);
  private readonly isBrowser: boolean;

  constructor(
    private readonly http: HttpClient,
    private readonly ui: UnifiedCartUIService,
    @Inject(PLATFORM_ID) platformId: object
  ) {
    this.isBrowser = isPlatformBrowser(platformId);
  }

  async hydrate(): Promise<void> {
    if (!this.isBrowser) return;
    const id = this.readId();
    if (!id) return;
    try {
      const cart = await firstValueFrom(
        this.http.get<DropshipCart>(`${API_BASE}/cart`, { params: { id } })
      );
      this.cart$.next(cart);
    } catch (err) {
      if (this.isStatus(err, 404)) {
        this.clearId();
        this.cart$.next(null);
      }
    }
  }

  activeCart$(): Observable<DropshipCart | null> {
    return this.cart$.asObservable();
  }

  get snapshot(): DropshipCart | null {
    return this.cart$.value;
  }

  itemCount(): number {
    return this.cart$.value?.lines.reduce((n, l) => n + l.quantity, 0) ?? 0;
  }

  async addLine(merchandiseId: string, quantity: number): Promise<DropshipCart> {
    let cartId = this.cart$.value?.id ?? this.readId();
    if (!cartId) {
      const fresh = await this.createCart();
      cartId = fresh.id;
    }
    let cart: DropshipCart;
    try {
      cart = await this.postLine(cartId, merchandiseId, quantity);
    } catch (err) {
      if (this.isStatus(err, 422)) {
        const fresh = await this.createCart();
        cart = await this.postLine(fresh.id, merchandiseId, quantity);
      } else {
        throw err;
      }
    }
    this.ui.open('exclusives', true);
    return cart;
  }

  async updateLine(lineId: string, quantity: number): Promise<DropshipCart> {
    const cartId = this.requireCartId();
    try {
      const cart = await firstValueFrom(
        this.http.patch<DropshipCart>(`${API_BASE}/cart/lines`, {
          cartId,
          lines: [{ id: lineId, quantity }]
        })
      );
      this.cart$.next(cart);
      return cart;
    } catch (err) {
      if (this.isStatus(err, 422)) {
        this.resetLocalCart();
        throw new CartResetError();
      }
      throw err;
    }
  }

  async removeLine(lineId: string): Promise<DropshipCart> {
    const cartId = this.requireCartId();
    try {
      const cart = await firstValueFrom(
        this.http.request<DropshipCart>('delete', `${API_BASE}/cart/lines`, {
          body: { cartId, lineIds: [lineId] }
        })
      );
      this.cart$.next(cart);
      return cart;
    } catch (err) {
      if (this.isStatus(err, 422)) {
        this.resetLocalCart();
        throw new CartResetError();
      }
      throw err;
    }
  }

  private resetLocalCart(): void {
    this.clearId();
    this.cart$.next(null);
  }

  private async createCart(): Promise<DropshipCart> {
    const body: DropshipCartCreateRequest = { attributes: CART_ATTRIBUTES };
    const cart = await firstValueFrom(
      this.http.post<DropshipCart>(`${API_BASE}/cart`, body)
    );
    this.persistId(cart.id);
    this.cart$.next(cart);
    return cart;
  }

  private async postLine(
    cartId: string,
    merchandiseId: string,
    quantity: number
  ): Promise<DropshipCart> {
    const cart = await firstValueFrom(
      this.http.post<DropshipCart>(`${API_BASE}/cart/lines`, {
        cartId,
        lines: [{ merchandiseId, quantity }]
      })
    );
    this.cart$.next(cart);
    return cart;
  }

  private requireCartId(): string {
    const id = this.cart$.value?.id;
    if (!id) throw new Error('No active exclusives cart');
    return id;
  }

  private readId(): string | null {
    if (!this.isBrowser) return null;
    try {
      return localStorage.getItem(STORAGE_KEY);
    } catch {
      return null;
    }
  }

  private persistId(id: string): void {
    if (!this.isBrowser) return;
    try {
      localStorage.setItem(STORAGE_KEY, id);
    } catch {
      // localStorage disabled (private mode, quota) — cart still works in-memory.
    }
  }

  private clearId(): void {
    if (!this.isBrowser) return;
    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch {
      // ignore
    }
  }

  private isStatus(err: unknown, status: number): boolean {
    return typeof err === 'object' && err !== null && (err as { status?: number }).status === status;
  }
}
